import PropTypes from 'prop-types'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { DateTime } from 'luxon'
import ContextMenuComponent from '../../../../../components/stories/organisms/context-menu'
import {
  contextMenuAlignOptions,
  contextMenuArrowAlignmentOptions,
  contextMenuPaddingOptions,
  contextMenuSideOptions,
  contextMenuTitleSizeOptions,
  contextMenuTitleWeightOptions,
  contextMenuVariantOptions
} from '../../../../../components/stories/organisms/context-menu/constants'
import { useUpdatePlanDetailsMutation } from '../../../../../react-query/plan/mutation/use-update-plan-details-mutation'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSnackbar } from '../../../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../../../components/stories/molecules/snackbar/constants'
import { getIsAdmin } from '../../../../../react-query/authentication/query/use-me-query'
import { useGetPlanDetailsQuery } from '../../../../../react-query/plan/query/use-get-plan-details-query'
import { SpinnerGrey } from '../../../../../components/stories/atoms/icons/special'
import { iconSizeOptions } from '../../../../../components/stories/atoms/icons/icons-sizes'
import {
  RevisionButtonContainer,
  RevisionListTitle
} from '../../../styled-component'
import ChangeRevisionConfirmModal from '../../change-revision-confirm-modal'
import { skipRevisionChangeConfirmationKey } from '../../../constants'

const REVISION_SKELETON_IDS = [
  'revision-skeleton-0',
  'revision-skeleton-1',
  'revision-skeleton-2'
]

const MENU_ESTIMATED_HEIGHT = 250

const PlanMenuSelectRevisionMenu = ({ item }) => {
  const { formatMessage, locale } = useIntl()
  const [isOpen, setIsOpen] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const { addSnackbar } = useSnackbar()
  const [selectedRevision, setSelectedRevision] = useState([])
  const isAdmin = getIsAdmin()

  const changeRevisionSuccess = () => {
    setIsConfirmModalOpen(false)
    setIsOpen(false)

    addSnackbar({
      message: formatMessage(
        { id: 'plans_message_revision-selected_success' },
        { number: selectedRevision[0]?.name }
      ),
      snackbarType: snackbarVariantOptions.SUCCESS,
      isOpen: true,
      hideCloseButton: true
    })
  }

  // import date shown under each revision number
  const formatImportDate = (date) => {
    if (!date) return null

    return DateTime.fromISO(date).setLocale(locale).toFormat('dd LLL yyyy')
  }

  const showLoadSpinner = (revision) => {
    if (isUpdatePending && selectedRevision[0]?.id === revision.id) {
      return <SpinnerGrey size={iconSizeOptions['ICON-S']} />
    }
  }

  // queries
  const { data: planDetails, isLoading: planDetailsLoading } =
    useGetPlanDetailsQuery(item.id, { enabled: isOpen })

  // mutations

  const { mutate: mutateUpdatePlanDetails, isPending: isUpdatePending } =
    useUpdatePlanDetailsMutation({
      onSuccess: changeRevisionSuccess
    })

  // helpers
  const revisionList = planDetails?.revisions ?? []
  const currentRevision = planDetails?.currentRevision

  // portal for context menu
  const triggerRef = useRef(null)

  const triggerRect =
    isOpen && triggerRef.current
      ? triggerRef.current.getBoundingClientRect()
      : null

  // Check if the context menu overlaps the bottom limit of the screen
  const openUpward = triggerRect
    ? window.innerHeight - triggerRect.bottom < MENU_ESTIMATED_HEIGHT
    : false

  // handlers
  const handleTriggerClick = useCallback((event) => {
    event.stopPropagation()
  }, [])

  const handleItemClick = (revision) => {
    if (revision.id !== currentRevision?.id) {
      setSelectedRevision([{ id: revision.id, name: revision.planName }])

      if (localStorage.getItem(skipRevisionChangeConfirmationKey)) {
        handleConfirmChange(revision)
      } else {
        setIsConfirmModalOpen(true)
        setIsOpen(false)
      }
    }
  }

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false)
  }

  const handleConfirmChange = (revision) => {
    mutateUpdatePlanDetails({
      planId: item.id,
      planDetails: { currentRevisionId: revision.id }
    })
  }

  // effects
  useEffect(() => {
    if (currentRevision?.id) {
      setSelectedRevision([
        { id: currentRevision.id, name: currentRevision.planName }
      ])
    }
  }, [currentRevision?.id])

  return (
    <>
      <ChangeRevisionConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={handleCloseConfirmModal}
        setSelectedRevision={setSelectedRevision}
        onConfirm={handleConfirmChange}
        newRevision={selectedRevision[0]}
        currentRevision={currentRevision}
        isLoading={isUpdatePending}
      />

      <div ref={triggerRef}>
        <ContextMenuComponent.Root
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          selectedItemsProp={selectedRevision}
          setSelectedItemsProp={setSelectedRevision}
          isLoading={planDetailsLoading}
          isSingle
        >
          <ContextMenuComponent.Trigger
            callback={handleTriggerClick}
            disabled={!isAdmin}
          >
            <RevisionButtonContainer
              type="button"
              isSelected={isOpen}
              disabled={!isAdmin}
            >
              {item?.version}
            </RevisionButtonContainer>
          </ContextMenuComponent.Trigger>
          {triggerRect &&
            createPortal(
              <div onMouseDown={(e) => e.stopPropagation()}>
                <ContextMenuComponent.List
                  width="160px"
                  align={contextMenuAlignOptions.START}
                  side={
                    openUpward
                      ? contextMenuSideOptions.TOP
                      : contextMenuSideOptions.BOTTOM
                  }
                  arrow
                  arrowAlignment={contextMenuArrowAlignmentOptions.START}
                  hideScrollbar
                  style={{
                    position: 'fixed',
                    top: openUpward ? 'auto' : triggerRect.bottom + 5,
                    bottom: openUpward
                      ? window.innerHeight - triggerRect.top
                      : 'auto',
                    left: triggerRect.left,
                    right: 'auto',
                    transform: 'none',
                    zIndex: 9999
                  }}
                >
                  <RevisionListTitle>
                    <FormattedMessage id="plans_table_revision_select_header" />
                  </RevisionListTitle>
                  {planDetailsLoading
                    ? REVISION_SKELETON_IDS.map((skeletonId) => (
                        <ContextMenuComponent.Item
                          key={skeletonId}
                          id={skeletonId}
                          padding={contextMenuPaddingOptions['EXTRA-LARGE']}
                          titleWeight={
                            contextMenuTitleWeightOptions['TEXT-500']
                          }
                          titleSize={contextMenuTitleSizeOptions['TEXT-R']}
                        />
                      ))
                    : revisionList?.map((revision) => (
                        <Fragment key={revision?.id}>
                          <ContextMenuComponent.Item
                            id={revision?.id}
                            variant={contextMenuVariantOptions.STANDARD}
                            padding={contextMenuPaddingOptions['EXTRA-LARGE']}
                            titleWeight={
                              contextMenuTitleWeightOptions['TEXT-500']
                            }
                            titleSize={contextMenuTitleSizeOptions['TEXT-R']}
                            onClick={() => handleItemClick(revision)}
                            title={revision?.version}
                            secondaryText={formatImportDate(revision?.created)}
                            trailingIcon={showLoadSpinner(revision)}
                          />
                        </Fragment>
                      ))}
                </ContextMenuComponent.List>
              </div>,
              document.body
            )}
        </ContextMenuComponent.Root>
      </div>
    </>
  )
}

PlanMenuSelectRevisionMenu.propTypes = {
  item: PropTypes.object
}

export default PlanMenuSelectRevisionMenu
